import { WorkflowConversationAction } from './WorkflowConversationPresentation';
import React from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import HomeChatWorkflowBanner from './HomeChatWorkflowBanner';




/**
 * Render the short interval between a confirmed Home Chat handoff and the
 * server returning its authoritative GPC controller view.  This deliberately
 * is not ``GpcChatPanel``: that component owns a bound controller session and
 * must never be mounted with a fabricated or empty session id.  Keeping the
 * optimistic opening here preserves the existing in-chat feedback without
 * exposing an unusable controller to consumers that render the panel
 * synchronously.
 */
/**
 * The initial GPC handoff runs before the controller returns its session
 * view, but it is still an ordinary owner-bound Home Chat request. Keep this
 * small status card connected to that request's progress envelope so it never
 * hides discovery work behind a generic "starting" message.
 */
export const GpcHandoffPending: React.FC<{
  openingMessage: string;
  progressLabel: string;
  processingAction: React.ReactNode;
  headerAction?: React.ReactNode;
}> = ({ openingMessage, progressLabel, processingAction, headerAction }) => (
  <Paper
    square
    elevation={0}
    sx={{
      height: '100%',
      minHeight: 0,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      gap: 0,
    }}
    data-testid="home-chat-gpc-handoff-pending"
  >
    <HomeChatWorkflowBanner
      title="GPC purchase assistant"
      subtitle="Answers are saved only after the required confirmation."
      action={headerAction}
    />
    <Box
      data-testid="gpc-chat-scroll-region"
      tabIndex={0}
      aria-label="GPC conversation and pending fields"
      aria-busy="true"
      sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch', pr: 0.5, pb: 0.5 }}
    >
      <WorkflowConversationAction />
      <Box
        data-testid="gpc-chat-message-user-0"
        data-message-role="user"
        sx={{ alignSelf: 'flex-end', ml: 'auto', maxWidth: '86%', px: 1.5, py: 1, borderRadius: 2, bgcolor: 'primary.main', color: 'primary.contrastText', whiteSpace: 'pre-wrap' }}
      >
        {openingMessage}
      </Box>
      <Box
        role="status"
        data-testid="gpc-chat-busy-status"
        sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary', px: 1, py: 1.5 }}
      >
        <CircularProgress size={18} aria-label={progressLabel} />
        <Typography variant="body2">{progressLabel}</Typography>
        {processingAction}
      </Box>
    </Box>
  </Paper>
);
